import { supabase } from '../lib/supabase';

const projectFileService = {
  /**
   * Get all files for a project with optional tag filter
   */
  async getProjectFiles(projectId, tagFilter = null) {
    try {
      let query = supabase?.from('project_files')
        ?.select('*')
        ?.eq('project_id', projectId)
        ?.order('created_at', { ascending: false });

      // Filter by tag if provided
      if (tagFilter) {
        query = query?.contains('tags', [tagFilter]);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching project files:', error);
      return [];
    }
  },

  /**
   * Get all unique tags used in a project
   */
  async getProjectTags(projectId) {
    try {
      const { data, error } = await supabase?.from('project_files')
        ?.select('tags')
        ?.eq('project_id', projectId);

      if (error) throw error;

      // Extract unique tags from all files
      const allTags = new Set();
      data?.forEach(file => {
        file?.tags?.forEach(tag => allTags?.add(tag));
      });

      return Array.from(allTags)?.sort();
    } catch (error) {
      console.error('Error fetching project tags:', error);
      return [];
    }
  },

  /**
   * Upload a file with optional tags
   */
  async uploadFile(projectId, file, tags = []) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      const userId = user?.user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Generate unique file path
      const timestamp = Date.now();
      const sanitizedFileName = file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${projectId}/${userId}/${timestamp}-${sanitizedFileName}`;

      // Upload to storage
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('project-files')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Create database record with tags
      const { data: fileRecord, error: dbError } = await supabase?.from('project_files')
        ?.insert({
          project_id: projectId,
          name: file?.name,
          file_path: filePath,
          file_size: file?.size,
          mime_type: file?.type,
          tags: tags || [],
          uploaded_by: userId
        })
        ?.select()
        ?.single();

      if (dbError) {
        // Cleanup storage if database insert fails
        await supabase?.storage?.from('project-files')?.remove([filePath]);
        throw dbError;
      }

      return { data: fileRecord, error: null };
    } catch (error) {
      console.error('Error uploading file:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Update tags for a file
   */
  async updateFileTags(fileId, tags) {
    try {
      const { data, error } = await supabase?.from('project_files')
        ?.update({ 
          tags: tags || [],
          updated_at: new Date()?.toISOString() 
        })
        ?.eq('id', fileId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating file tags:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Get signed URL for private file
   */
  async getFileUrl(filePath, expiresIn = 3600) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('project-files')
        ?.createSignedUrl(filePath, expiresIn);

      if (error) throw error;
      return data?.signedUrl || null;
    } catch (error) {
      console.error('Error getting file URL:', error);
      return null;
    }
  },

  /**
   * Delete a file
   */
  async deleteFile(fileId, filePath) {
    try {
      // Delete from storage
      if (filePath) {
        const { error: storageError } = await supabase?.storage
          ?.from('project-files')
          ?.remove([filePath]);

        if (storageError) {
          console.warn('Storage deletion warning:', storageError);
        }
      }

      // Delete database record
      const { error: dbError } = await supabase?.from('project_files')
        ?.delete()
        ?.eq('id', fileId);

      if (dbError) throw dbError;
      return { error: null };
    } catch (error) {
      console.error('Error deleting file:', error);
      return { error: error?.message };
    }
  },

  /**
   * Rename a file
   */
  async renameFile(fileId, newName) {
    try {
      const { data, error } = await supabase?.from('project_files')
        ?.update({ 
          name: newName, 
          updated_at: new Date()?.toISOString() 
        })
        ?.eq('id', fileId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error renaming file:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Download a file
   */
  async downloadFile(filePath, fileName) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('project-files')
        ?.download(filePath);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);

      return { error: null };
    } catch (error) {
      console.error('Error downloading file:', error);
      return { error: error?.message };
    }
  },

  /**
   * Search files by name or tags
   */
  async searchFiles(projectId, searchTerm) {
    try {
      const { data, error } = await supabase?.from('project_files')
        ?.select('*')
        ?.eq('project_id', projectId)
        ?.or(`name.ilike.%${searchTerm}%,tags.cs.{${searchTerm}}`)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching files:', error);
      return [];
    }
  }
};

export default projectFileService;